import * as Sentry from "@sentry/vue";

type SessionData = Record<string, any> | null;

export function useSessionData() {
  const { uuid } = useHelpers();
  const sessionId = useCookie("NUXT_SESSION_ID");
  let sessionData: SessionData = null;

  if (!sessionId.value) {
    sessionId.value = uuid();
  }

  const redisKey = `session-${sessionId.value}`;

  const getData = async (): Promise<SessionData> => {
    try {
      const response = await $fetch("/api/redis", {
        method: "GET",
        params: { key: redisKey },
      });

      if (
        response.success &&
        "value" in response &&
        typeof response.value === "string"
      ) {
        return JSON.parse(response.value);
      } else {
        return null;
      }
    } catch (error) {
      Sentry.captureEvent({
        message: `Failed to fetch session data`,
        level: "warning",
      });

      return null;
    }
  };

  const persistData = async () => {
    try {
      const response = await $fetch("/api/redis", {
        method: "POST",
        body: {
          key: redisKey,
          value: sessionData === null ? null : JSON.stringify(sessionData),
          ttl: 60 * 60, // 1 hour
        },
      });

      if (!response.success) {
        Sentry.captureEvent({
          message: `Failed to persist session data`,
          level: "error",
        });
      }

      return response.success;
    } catch (error) {
      Sentry.captureEvent({
        message: `Failed to persist session data`,
        level: "error",
      });

      return false;
    }
  };

  const getItem = async (key: string) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      return null;
    }

    if (key in sessionData) {
      return sessionData[key];
    } else {
      return null;
    }
  };

  const setItem = async (key: string, value: any) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      sessionData = {};
    }

    sessionData[key] = value;
    await persistData();
  };

  const setItems = async (newItems: SessionData) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      sessionData = {};
    }

    sessionData = Object.assign(sessionData, newItems);
    await persistData();
  };

  const terminate = async () => {
    sessionData = null;
    await persistData();
  };

  const removeItem = async (key: string) => {
    if (sessionData === null) {
      sessionData = await getData();
    }

    if (!sessionData) {
      sessionData = {};
    }

    delete sessionData[key];

    if (Object.keys(sessionData).length === 0) {
      // Terminate session
      sessionData = null;
    }

    await persistData();
  };

  return {
    getItem,
    setItem,
    setItems,
    removeItem,
    terminate,
  };
}
